import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A treemap is a hierarchical data visualization technique that uses nested rectangles to
          represent data. Each branch of the hierarchy is represented by a colored rectangle, and
          its size is proportional to a specific metric, such as volume or count. Within each
          rectangle, smaller rectangles represent subcategories, allowing for a compact display of
          large datasets while maintaining an organized structure. Treemaps are especially effective
          for showing proportions, part-to-whole relationships, and distributions within
          hierarchical structures, making them useful for visualizing complex data like sales
          performance, website traffic, or organizational structures.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Treemaps are a versatile tool for presenting hierarchical and proportional data in a
          visually compact manner. Here are some significant uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Displaying Hierarchical Structures:</strong> Treemaps represent nested
            relationships within a dataset, making it easier to understand complex hierarchies, such
            as organizational charts or file systems.
          </li>
          <li>
            <strong>Comparing Proportions:</strong> They excel at showing the relative sizes of
            categories and subcategories, such as market share or sales distribution, within a
            confined space.
          </li>
          <li>
            <strong>Maximizing Space Efficiency:</strong> Treemaps use available space effectively,
            making them ideal for displaying large datasets with numerous categories and
            subcategories on a single visual.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Treemaps are effective for visualizing hierarchical data and proportional comparisons but
          face several important limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty with Small or Similar Values:</strong> When multiple categories have
            small or similar values, the corresponding rectangles can become too tiny to see or
            differentiate. This can obscure meaningful insights and make it difficult for users to
            interpret the data accurately.
          </li>
          <li>
            <strong>Limited Depth Representation:</strong> Treemaps struggle to effectively display
            deeply nested hierarchies. Beyond a certain level, the visual clarity diminishes, and
            users may find it hard to navigate or understand the relationships between layers.
          </li>
          <li>
            <strong>Challenging Label Placement:</strong> Adding readable labels to the rectangles
            is often problematic, especially for smaller areas. Overlapping or truncated labels can
            confuse viewers and detract from the overall interpretability of the chart.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in treemaps is essential to convey data effectively and maintain visual
          clarity. Distinct color schemes can differentiate categories or hierarchies, making it
          easier to identify patterns and groupings. Gradient palettes work well to represent
          numerical values or intensities, with lighter shades indicating smaller values and darker
          shades larger ones. Balanced and thoughtful color choices ensure the treemap remains
          visually appealing while emphasizing key data insights.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Treemaps are a powerful tool for displaying hierarchical data, but effectively designing
          and interpreting them comes with certain challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Detail Representation:</strong> Treemaps display data as rectangles,
            which can limit the level of detail shown. Small sections may become unreadable, making
            it difficult to present precise values or intricate information.
          </li>
          <li>
            <strong>Hierarchy Complexity:</strong> Visualizing deeply nested hierarchies can result
            in a cluttered appearance, making it challenging to discern relationships or patterns
            among data categories.
          </li>
          <li>
            <strong>Color and Size Misinterpretation:</strong> The reliance on size and color to
            represent data can lead to misinterpretation. Subtle differences in these attributes may
            not be immediately apparent, affecting the user&apos;s ability to draw accurate
            conclusions.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
